import AddIcon from '@mui/icons-material/Add';
import { Box, Button, IconButton, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import React, { useCallback, useEffect, useRef } from 'react';

import GridItemCount from '@sophocles/data-grids/components/cells/GridItemCount';
import useGridQuickFilter from '@sophocles/data-grids/datasource/useGridQuickFilter';

import DataGridToolbarShell from './DataGridToolbarShell';
import ToolbarSearchInput from './ToolbarSearchInput';

const QUICK_FILTER_DEBOUNCE_MS = 300;

function ToolbarSearch({ placeholder, isMobile, apiRef, itemCount, countNoun }) {
  const [value, setValue, commit] = useGridQuickFilter(apiRef);
  const inputRef = useRef(null);

  // Debounced auto-commit. The hook already keeps `value` in sync with
  // the grid (incl. initial sessionStorage restore), so re-committing a
  // synced value is a no-op for MUI's idempotent setQuickFilterValues.
  useEffect(() => {
    const timer = setTimeout(() => commit(value), QUICK_FILTER_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [value, commit]);

  const handleKeyDown = useCallback(
    e => {
      if (e.key === 'Escape' && value) setValue('');
    },
    [value],
  );

  // Persistent, full-width filled search field on every viewport. On mobile
  // it sits on its own toolbar row (see DataGridToolbar) and gets a slightly
  // softer corner radius — replacing the old tap-to-expand icon, which
  // animated a cramped field open inside the toolbar row and read poorly.
  return (
    <ToolbarSearchInput
      inputRef={inputRef}
      value={value}
      onChange={setValue}
      placeholder={placeholder}
      onKeyDown={handleKeyDown}
      itemCount={itemCount}
      countNoun={countNoun}
      radius={isMobile ? 'var(--radius-md)' : undefined}
    />
  );
}

function CreateActionButton({ createAction, isMobile }) {
  if (!createAction) return null;
  const { label, onClick, icon = <AddIcon />, color = 'primary' } = createAction;

  if (isMobile) {
    return (
      <Tooltip title={label}>
        <IconButton
          color={color}
          onClick={onClick}
          aria-label={label}
          sx={{
            bgcolor: theme => theme.palette[color]?.main,
            color: theme => theme.palette[color]?.contrastText,
            '&:hover': {
              bgcolor: theme => theme.palette[color]?.dark,
            },
          }}
        >
          {icon}
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <Button variant="contained" size="small" color={color} startIcon={icon} onClick={onClick} sx={{ flexShrink: 0 }}>
      {label}
    </Button>
  );
}

function DataGridToolbar({
  apiRef,
  toolbarContent,
  toolbarActions,
  createAction,
  showQuickFilter = true,
  quickFilterPlaceholder,
  // Optional override for the search slot. When provided, replaces the
  // built-in ToolbarSearch in the same position. `showQuickFilter` is
  // ignored when `searchSlot` is set — consumers fully own the input; the
  // toolbar injects the item count into it (itemCount/countNoun props) so
  // the count reads as the same in-field suffix as the built-in search.
  searchSlot,
  totalRowCount,
  // Optional already-translated plural noun for the row count ("24 batches").
  // Falls back to the generic "24 items" when omitted.
  countNoun,
  variant = 'elevated',
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // The total-item count rides inside the search field (built-in or the
  // consumer's searchSlot). Only fall back to the action cluster when the
  // grid has no search to host it.
  const builtinSearch = !searchSlot && showQuickFilter;
  const showFallbackCount = !searchSlot && !showQuickFilter && totalRowCount != null;
  // When a flex-growing search is present it should fill the row on mobile,
  // so the trailing action cluster must drop its `ml: auto` there (an auto
  // margin would otherwise eat the free space and collapse the search field).
  const hasGrowingSearch = Boolean(searchSlot) || builtinSearch;

  return (
    <DataGridToolbarShell variant={variant}>
      {toolbarContent && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--toolbar-gap)',
            flexWrap: 'wrap',
            minWidth: 0,
            // On mobile the filter area takes a full row so its chips wrap
            // within the viewport instead of overflowing; desktop keeps the
            // intrinsic-width, inline-with-search layout.
            flexBasis: { xs: '100%', md: 'auto' },
            flexShrink: { xs: 1, md: 0 },
          }}
        >
          {toolbarContent}
        </Box>
      )}

      {searchSlot ? (
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }}>
          {/* The count rides inside the consumer's search input as a suffix
              (same placement as the built-in search) — injected here so slot
              consumers never have to plumb totalRowCount themselves. */}
          {totalRowCount != null ? React.cloneElement(searchSlot, { itemCount: totalRowCount, countNoun }) : searchSlot}
        </Box>
      ) : (
        builtinSearch && (
          <ToolbarSearch
            placeholder={quickFilterPlaceholder}
            isMobile={isMobile}
            apiRef={apiRef}
            itemCount={totalRowCount}
            countNoun={countNoun}
          />
        )
      )}

      {(showFallbackCount || toolbarActions || createAction) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--toolbar-gap)',
            flexShrink: 0,
            ml: hasGrowingSearch ? { xs: 0, md: 'auto' } : 'auto',
          }}
        >
          {showFallbackCount && <GridItemCount count={totalRowCount} noun={countNoun} />}
          {toolbarActions}
          <CreateActionButton createAction={createAction} isMobile={isMobile} />
        </Box>
      )}
    </DataGridToolbarShell>
  );
}

export default DataGridToolbar;
